import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Building2, Users } from 'lucide-react';
import { useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import EntityAutocomplete from '../Common/EntityAutocomplete';
import FormModalLayout from '../Common/FormModalLayout';
import ClientModal from '../Identity/ClientModal';
import Can from '../Common/Can';
import { useCrud, useApiQuery } from '../../hooks/useApi';
import { useLookupMaps } from '../../hooks/useLookupMaps';
import { usePermissions } from '../../hooks/usePermissions';
import { identityService, institutionService } from '../../services/api';
import { P } from '../../constants/permissions';
import { userFacingError } from '../../i18n/userFacingError';

function createdId(payload) {
  return payload?.id ?? payload?.data?.id ?? null;
}

export function blankVinculo(value) {
  return value ? value : null;
}

/**
 * Órgão, setor, servidor e requerente do processo. Nenhum é obrigatório.
 * O + abre o cadastro e, ao salvar, volta com o registro selecionado.
 */
export default function ProcessoVinculoFields({
  idPrefix,
  form,
  setForm,
  party,
  clientsQueryKey,
  nestedZIndexClass = 'z-[60]',
}) {
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canInstitution = hasPermission(P.INSTITUTION_ACCESS);
  const canCliente = hasPermission(P.IDENTITY_ACCESS);
  const [child, setChild] = useState(null);
  const [saving, setSaving] = useState(false);
  const [orgaoNome, setOrgaoNome] = useState('');
  const [orgaoRouting, setOrgaoRouting] = useState(false);
  const [setorNome, setSetorNome] = useState('');
  const [setorOrgaoId, setSetorOrgaoId] = useState('');
  const [setorRouting, setSetorRouting] = useState(false);
  const [servidor, setServidor] = useState({
    person_id: '',
    setor_id: '',
    cargo_id: '',
    matricula: '',
  });

  const { useList: useOrgaosList } = useCrud('orgaos', institutionService.orgaoCrud);
  const { useList: useSetoresList } = useCrud('setores', institutionService.setorCrud);
  const { data: orgaosData } = useOrgaosList();
  const { data: setoresData } = useSetoresList();
  const orgaos = useMemo(
    () => (Array.isArray(orgaosData) ? orgaosData : orgaosData?.data ?? []),
    [orgaosData]
  );
  const setores = useMemo(
    () => (Array.isArray(setoresData) ? setoresData : setoresData?.data ?? []),
    [setoresData]
  );

  const { data: clientsPayload } = useApiQuery(clientsQueryKey, () =>
    identityService.getClients({ limit: 1000 }).then((r) => r.data)
  );
  const clients = useMemo(
    () => (Array.isArray(clientsPayload) ? clientsPayload : clientsPayload?.data ?? []),
    [clientsPayload]
  );

  const { data: servidoresNoSetor } = useApiQuery(
    ['servidores', 'setor', form.setor_origem_id, idPrefix],
    () => institutionService.getServidoresBySetor(form.setor_origem_id).then((r) => r.data),
    { enabled: !!form.setor_origem_id }
  );
  const servidoresList = useMemo(
    () => (Array.isArray(servidoresNoSetor) ? servidoresNoSetor : servidoresNoSetor?.data ?? []),
    [servidoresNoSetor]
  );

  const { data: cargosRaw } = useApiQuery(
    ['cargos', 'processo-vinculo'],
    () => institutionService.cargoCrud.getList({ limit: 500 }),
    { enabled: child === 'servidor' }
  );
  const cargos = Array.isArray(cargosRaw) ? cargosRaw : cargosRaw?.data ?? [];
  const { personOptions } = useLookupMaps({ persons: child === 'servidor' });

  const orgaoOptions = useMemo(
    () => orgaos.map((o) => ({ id: o.id, label: o.name || o.id })),
    [orgaos]
  );
  const setorOptions = useMemo(
    () =>
      setores
        .filter((s) => !form.orgao_origem_id || s.orgao_id === form.orgao_origem_id)
        .map((s) => ({ id: s.id, label: s.name || s.id })),
    [setores, form.orgao_origem_id]
  );
  const servidorOptions = useMemo(
    () =>
      servidoresList.map((s) => {
        const setorNomeItem = setores.find((x) => x.id === s.setor_id)?.name || '';
        const mat = s.matricula || '—';
        return { id: s.id, label: setorNomeItem ? `${mat} · ${setorNomeItem}` : mat };
      }),
    [servidoresList, setores]
  );
  const clientOptions = useMemo(
    () =>
      clients.map((c) => {
        const doc = c.document || c.cpf || c.cnpj || '';
        const base = c.name || '—';
        return { id: c.id, label: doc ? `${base} · ${doc}` : base };
      }),
    [clients]
  );

  const openOrgao = () => {
    setOrgaoNome('');
    setOrgaoRouting(false);
    setChild('orgao');
  };
  const openSetor = () => {
    setSetorNome('');
    setSetorOrgaoId(form.orgao_origem_id || '');
    setSetorRouting(false);
    setChild('setor');
  };
  const openServidor = () => {
    setServidor({
      person_id: '',
      setor_id: form.setor_origem_id || '',
      cargo_id: '',
      matricula: '',
    });
    setChild('servidor');
  };

  const saveOrgao = async (e) => {
    e.preventDefault();
    const name = orgaoNome.trim();
    if (!name) {
      toast.error('Informe o nome do órgão.');
      return;
    }
    setSaving(true);
    try {
      const created = await institutionService.orgaoCrud.create({
        name,
        receive_routing: orgaoRouting,
      });
      const id = createdId(created);
      if (!id) {
        toast.error('Órgão criado, mas a resposta não trouxe o identificador.');
        return;
      }
      await queryClient.invalidateQueries(['orgaos']);
      setForm((f) => ({
        ...f,
        orgao_origem_id: id,
        setor_origem_id: '',
        usuario_id: '',
      }));
      setChild(null);
      toast.success('Órgão cadastrado e selecionado');
    } catch (err) {
      toast.error(userFacingError(err, 'saveError'));
    } finally {
      setSaving(false);
    }
  };

  const saveSetor = async (e) => {
    e.preventDefault();
    const name = setorNome.trim();
    if (!name || !setorOrgaoId) {
      toast.error('Informe o nome do setor e o órgão.');
      return;
    }
    setSaving(true);
    try {
      const created = await institutionService.setorCrud.create({
        name,
        orgao_id: setorOrgaoId,
        receive_routing: setorRouting,
      });
      const id = createdId(created);
      if (!id) {
        toast.error('Setor criado, mas a resposta não trouxe o identificador.');
        return;
      }
      await queryClient.invalidateQueries(['setores']);
      setForm((f) => ({
        ...f,
        orgao_origem_id: setorOrgaoId,
        setor_origem_id: id,
        usuario_id: '',
      }));
      setChild(null);
      toast.success('Setor cadastrado e selecionado');
    } catch (err) {
      toast.error(userFacingError(err, 'saveError'));
    } finally {
      setSaving(false);
    }
  };

  const saveServidor = async (e) => {
    e.preventDefault();
    if (!servidor.person_id || !servidor.setor_id || !servidor.cargo_id) {
      toast.error('Selecione pessoa, setor e cargo.');
      return;
    }
    setSaving(true);
    try {
      const created = await institutionService.servidorCrud.create({
        person_id: servidor.person_id,
        setor_id: servidor.setor_id,
        cargo_id: servidor.cargo_id,
        matricula: servidor.matricula.trim() || undefined,
        perfil: 2,
        receive_routing: true,
      });
      const id = createdId(created);
      if (!id) {
        toast.error('Servidor criado, mas a resposta não trouxe o identificador.');
        return;
      }
      const setor = setores.find((item) => item.id === servidor.setor_id);
      await queryClient.invalidateQueries(['servidores']);
      setForm((f) => ({
        ...f,
        usuario_id: id,
        setor_origem_id: servidor.setor_id,
        orgao_origem_id: setor?.orgao_id || f.orgao_origem_id,
      }));
      setChild(null);
      toast.success('Servidor cadastrado e selecionado');
    } catch (err) {
      toast.error(userFacingError(err, 'saveError'));
    } finally {
      setSaving(false);
    }
  };

  const saveCliente = async (clientData) => {
    try {
      const res = await identityService.createClient(clientData);
      const id = createdId(res.data);
      if (!id) {
        toast.error(`${party.One} criado, mas a resposta não trouxe o identificador.`);
        return;
      }
      await queryClient.invalidateQueries(clientsQueryKey);
      setForm((f) => ({ ...f, requerente_id: id }));
      setChild(null);
      toast.success(`${party.One} cadastrado e selecionado`);
    } catch (err) {
      toast.error(userFacingError(err, 'identity:clientCreateError'));
    }
  };

  const portal = (node) => (typeof document === 'undefined' ? node : createPortal(node, document.body));

  return (
    <>
      <div className="grid grid-cols-1 gap-4">
        <EntityAutocomplete
          id={`${idPrefix}-orgao`}
          label="Órgão de origem"
          value={form.orgao_origem_id}
          onChange={(id) =>
            setForm((f) => ({
              ...f,
              orgao_origem_id: id,
              setor_origem_id: '',
              usuario_id: '',
            }))
          }
          options={orgaoOptions}
          placeholder="Buscar órgão pelo nome…"
          onAddNew={canInstitution ? openOrgao : undefined}
          addNewLabel="Novo órgão…"
        />
        <EntityAutocomplete
          id={`${idPrefix}-setor`}
          label="Setor de origem"
          value={form.setor_origem_id}
          onChange={(id) => {
            const setor = setores.find((item) => item.id === id);
            setForm((f) => ({
              ...f,
              setor_origem_id: id,
              usuario_id: '',
              orgao_origem_id: setor?.orgao_id || f.orgao_origem_id,
            }));
          }}
          options={setorOptions}
          placeholder={form.orgao_origem_id ? 'Buscar setor pelo nome…' : 'Buscar setor ou cadastrar com +'}
          emptyMessage={form.orgao_origem_id ? 'Nenhum setor neste órgão' : 'Nenhum setor encontrado'}
          onAddNew={canInstitution ? openSetor : undefined}
          addNewLabel="Novo setor…"
        />
        <EntityAutocomplete
          id={`${idPrefix}-usuario`}
          label="Servidor responsável"
          value={form.usuario_id}
          onChange={(id) => setForm((f) => ({ ...f, usuario_id: id }))}
          options={servidorOptions}
          placeholder={
            form.setor_origem_id ? 'Buscar por matrícula ou setor…' : 'Buscar servidor ou cadastrar com +'
          }
          disabled={!form.setor_origem_id}
          keepAddEnabled
          emptyMessage={form.setor_origem_id ? 'Nenhum servidor neste setor' : 'Escolha um setor ou cadastre um servidor'}
          onAddNew={canInstitution ? openServidor : undefined}
          addNewLabel="Novo servidor…"
        />
        <EntityAutocomplete
          id={`${idPrefix}-req`}
          label={party.One}
          value={form.requerente_id}
          onChange={(id) => setForm((f) => ({ ...f, requerente_id: id }))}
          options={clientOptions}
          placeholder={`Buscar ${party.one} por nome ou documento…`}
          onAddNew={canCliente ? () => setChild('cliente') : undefined}
          addNewLabel={`Novo ${party.one}…`}
        />
      </div>

      {child === 'orgao'
        ? portal(
            <FormModalLayout
              zIndexClass={nestedZIndexClass}
              title="Novo órgão"
              subtitle="O órgão criado será selecionado no processo."
              headerIcon={Building2}
              onClose={() => {
                if (!saving) setChild(null);
              }}
              size="md"
              footer={
                <>
                  <button type="button" className="btn btn-outline" disabled={saving} onClick={() => setChild(null)}>
                    Cancelar
                  </button>
                  <Can permission={P.INSTITUTION_ACCESS}>
                    <button type="submit" form={`${idPrefix}-orgao-form`} className="btn btn-primary" disabled={saving}>
                      {saving ? 'Salvando…' : 'Criar e selecionar'}
                    </button>
                  </Can>
                </>
              }
            >
              <form id={`${idPrefix}-orgao-form`} onSubmit={saveOrgao} className="space-y-4">
                <div className="form-group">
                  <label className="form-label form-label-required" htmlFor={`${idPrefix}-orgao-nome`}>
                    Nome do órgão
                  </label>
                  <input
                    id={`${idPrefix}-orgao-nome`}
                    className="form-input"
                    value={orgaoNome}
                    onChange={(e) => setOrgaoNome(e.target.value)}
                    autoFocus
                  />
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    className="form-check"
                    checked={orgaoRouting}
                    onChange={(e) => setOrgaoRouting(e.target.checked)}
                  />
                  Receber tramitação
                </label>
              </form>
            </FormModalLayout>
          )
        : null}

      {child === 'setor'
        ? portal(
            <FormModalLayout
              zIndexClass={nestedZIndexClass}
              title="Novo setor"
              subtitle="O setor criado será selecionado no processo."
              headerIcon={Building2}
              onClose={() => {
                if (!saving) setChild(null);
              }}
              size="md"
              footer={
                <>
                  <button type="button" className="btn btn-outline" disabled={saving} onClick={() => setChild(null)}>
                    Cancelar
                  </button>
                  <Can permission={P.INSTITUTION_ACCESS}>
                    <button type="submit" form={`${idPrefix}-setor-form`} className="btn btn-primary" disabled={saving}>
                      {saving ? 'Salvando…' : 'Criar e selecionar'}
                    </button>
                  </Can>
                </>
              }
            >
              <form id={`${idPrefix}-setor-form`} onSubmit={saveSetor} className="space-y-4">
                <div className="form-group">
                  <label className="form-label form-label-required" htmlFor={`${idPrefix}-setor-nome`}>
                    Nome do setor
                  </label>
                  <input
                    id={`${idPrefix}-setor-nome`}
                    className="form-input"
                    value={setorNome}
                    onChange={(e) => setSetorNome(e.target.value)}
                    autoFocus
                  />
                </div>
                <div className="form-group">
                  <label className="form-label form-label-required" htmlFor={`${idPrefix}-setor-orgao`}>
                    Órgão
                  </label>
                  <select
                    id={`${idPrefix}-setor-orgao`}
                    className="form-select"
                    value={setorOrgaoId}
                    onChange={(e) => setSetorOrgaoId(e.target.value)}
                  >
                    <option value="">Selecione o órgão</option>
                    {orgaos.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.name || o.id}
                      </option>
                    ))}
                  </select>
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    className="form-check"
                    checked={setorRouting}
                    onChange={(e) => setSetorRouting(e.target.checked)}
                  />
                  Receber tramitação
                </label>
              </form>
            </FormModalLayout>
          )
        : null}

      {child === 'servidor'
        ? portal(
            <FormModalLayout
              zIndexClass={nestedZIndexClass}
              title="Novo servidor"
              subtitle="O servidor criado será selecionado no processo."
              headerIcon={Users}
              onClose={() => {
                if (!saving) setChild(null);
              }}
              size="lg"
              footer={
                <>
                  <button type="button" className="btn btn-outline" disabled={saving} onClick={() => setChild(null)}>
                    Cancelar
                  </button>
                  <Can permission={P.INSTITUTION_ACCESS}>
                    <button type="submit" form={`${idPrefix}-srv-form`} className="btn btn-primary" disabled={saving}>
                      {saving ? 'Salvando…' : 'Criar e selecionar'}
                    </button>
                  </Can>
                </>
              }
            >
              <form id={`${idPrefix}-srv-form`} onSubmit={saveServidor} className="space-y-4">
                <EntityAutocomplete
                  id={`${idPrefix}-srv-person`}
                  label="Pessoa"
                  required
                  value={servidor.person_id}
                  onChange={(id) => setServidor((current) => ({ ...current, person_id: id }))}
                  options={personOptions}
                  placeholder="Buscar pessoa pelo nome…"
                />
                <div className="form-group">
                  <label className="form-label form-label-required" htmlFor={`${idPrefix}-srv-setor`}>
                    Setor
                  </label>
                  <select
                    id={`${idPrefix}-srv-setor`}
                    className="form-select"
                    value={servidor.setor_id}
                    onChange={(e) => setServidor((current) => ({ ...current, setor_id: e.target.value }))}
                  >
                    <option value="">Selecione o setor</option>
                    {setores.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name || s.id}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label form-label-required" htmlFor={`${idPrefix}-srv-cargo`}>
                    Cargo
                  </label>
                  <select
                    id={`${idPrefix}-srv-cargo`}
                    className="form-select"
                    value={servidor.cargo_id}
                    onChange={(e) => setServidor((current) => ({ ...current, cargo_id: e.target.value }))}
                  >
                    <option value="">Selecione o cargo</option>
                    {cargos.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name || c.id}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor={`${idPrefix}-srv-mat`}>
                    Matrícula
                  </label>
                  <input
                    id={`${idPrefix}-srv-mat`}
                    className="form-input"
                    value={servidor.matricula}
                    onChange={(e) => setServidor((current) => ({ ...current, matricula: e.target.value }))}
                  />
                </div>
              </form>
            </FormModalLayout>
          )
        : null}

      {child === 'cliente'
        ? portal(
            <ClientModal
              zIndexClass={nestedZIndexClass}
              client={null}
              onClose={() => setChild(null)}
              onSubmit={saveCliente}
            />
          )
        : null}
    </>
  );
}
